import * as Matter from 'matter-js';
import { PositionedBall } from './urn.model';

const WALL_THICKNESS = 10;
const JAR_CENTER = { x: 110, y: 174 };
const WALL_OPTS: Matter.IChamferableBodyDefinition = { isStatic: true, friction: 0.5, restitution: 0.05 };

/**
 * Half the SVG stroke-width on ball circles (1.2 / 2 = 0.6).
 * Added to the physics radius so collision boundaries match the visual edges.
 */
const STROKE_HALF = 0.6;

/**
 * Create a wall segment between two points, offset outward so its inner
 * surface aligns with the visual jar edge (not inset by half the thickness).
 */
function wallSegment(x1: number, y1: number, x2: number, y2: number): Matter.Body {
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);

  // Normal perpendicular to segment
  let nx = -dy / len;
  let ny = dx / len;

  // Ensure normal points outward (away from jar interior)
  const midX = (x1 + x2) / 2;
  const midY = (y1 + y2) / 2;
  if (nx * (JAR_CENTER.x - midX) + ny * (JAR_CENTER.y - midY) > 0) {
    nx = -nx;
    ny = -ny;
  }

  // Offset center outward by half wall thickness so inner surface aligns with the visual edge
  const offset = WALL_THICKNESS / 2;
  const cx = midX + nx * offset;
  const cy = midY + ny * offset;
  const angle = Math.atan2(dy, dx);

  return Matter.Bodies.rectangle(cx, cy, len + WALL_THICKNESS, WALL_THICKNESS, { ...WALL_OPTS, angle });
}

function createJarWalls(): Matter.Body[] {
  return [
    // Neck
    wallSegment(68, 15, 68, 42),
    wallSegment(152, 15, 152, 42),
    // Left shoulder (approximate curve with segments)
    wallSegment(68, 42, 50, 60),
    wallSegment(50, 60, 32, 78),
    wallSegment(32, 78, 22, 90),
    // Right shoulder
    wallSegment(152, 42, 170, 60),
    wallSegment(170, 60, 188, 78),
    wallSegment(188, 78, 198, 90),
    // Straight sides
    wallSegment(22, 90, 22, 286),
    wallSegment(198, 90, 198, 286),
    // Bottom-left curve (finer segments to prevent balls catching on corners)
    wallSegment(22, 286, 24, 293),
    wallSegment(24, 293, 28, 300),
    wallSegment(28, 300, 33, 306),
    wallSegment(33, 306, 39, 311),
    wallSegment(39, 311, 46, 315),
    wallSegment(46, 315, 58, 318),
    // Bottom flat
    wallSegment(58, 318, 162, 318),
    // Bottom-right curve (finer segments)
    wallSegment(162, 318, 174, 315),
    wallSegment(174, 315, 181, 311),
    wallSegment(181, 311, 187, 306),
    wallSegment(187, 306, 192, 300),
    wallSegment(192, 300, 196, 293),
    wallSegment(196, 293, 198, 286),
  ];
}

export interface SettleSimulation {
  readonly engine: Matter.Engine;
  readonly bodies: Matter.Body[];
  readonly runner: Matter.Runner;
}

/**
 * Create a Matter.js simulation for settling balls after one is removed.
 * Balls at or below `removedCy` are pinned static — they cannot be affected by the removal.
 */
export function createSettleSimulation(balls: PositionedBall[], removedCy?: number): SettleSimulation {
  const engine = Matter.Engine.create({
    gravity: { x: 0, y: 1.5, scale: 0.001 },
    enableSleeping: true,
    positionIterations: 10,
    velocityIterations: 8,
  });

  Matter.Composite.add(engine.world, createJarWalls());

  const bodies = balls.map((ball) => {
    const pin = removedCy !== undefined && ball.cy >= removedCy;
    return Matter.Bodies.circle(ball.cx, ball.cy, ball.r + STROKE_HALF, {
      restitution: 0.1,
      friction: 0.4,
      frictionAir: 0.02,
      density: 0.004,
      sleepThreshold: 30,
      isStatic: pin,
    });
  });
  Matter.Composite.add(engine.world, bodies);

  const runner = Matter.Runner.create();

  return { engine, bodies, runner };
}

/**
 * Step the simulation forward by one frame (~16ms).
 * Static balls keep their original positions to avoid floating-point drift.
 */
export function stepSimulation(sim: SettleSimulation, balls: PositionedBall[]): PositionedBall[] {
  Matter.Engine.update(sim.engine, 1000 / 60);

  return balls.map((ball, i) => {
    if (sim.bodies[i].isStatic) {
      return ball;
    }
    return {
      ...ball,
      cx: sim.bodies[i].position.x,
      cy: sim.bodies[i].position.y,
    };
  });
}

/** Check if all dynamic bodies have come to rest. */
export function isSettled(sim: SettleSimulation): boolean {
  return sim.bodies.every((b) => b.isStatic || b.speed < 0.1);
}

/** Clean up the simulation. */
export function destroySimulation(sim: SettleSimulation): void {
  Matter.Engine.clear(sim.engine);
  Matter.Runner.stop(sim.runner);
}

/**
 * Create a simulation for remaining balls to fill the gap left by a removed ball.
 * All balls are dynamic with low friction and an initial downward nudge
 * to break the static equilibrium of the packed configuration.
 */
export function createGapFillSimulation(balls: PositionedBall[]): SettleSimulation {
  const engine = Matter.Engine.create({
    gravity: { x: 0, y: 2, scale: 0.001 },
    enableSleeping: true,
    positionIterations: 10,
    velocityIterations: 8,
  });

  Matter.Composite.add(engine.world, createJarWalls());

  const bodies = balls.map((ball) =>
    Matter.Bodies.circle(ball.cx, ball.cy, ball.r + STROKE_HALF, {
      restitution: 0.05,
      friction: 0.1,
      frictionAir: 0.03,
      density: 0.004,
      sleepThreshold: 60,
    }),
  );

  for (const body of bodies) {
    Matter.Body.setVelocity(body, { x: 0, y: 1.5 });
  }

  Matter.Composite.add(engine.world, bodies);
  const runner = Matter.Runner.create();
  return { engine, bodies, runner };
}

/**
 * Create a simulation where a single ball rises out of the jar (reversed gravity).
 * Only jar walls constrain the ball — other balls are ignored so it doesn't get stuck.
 */
export function createRiseSimulation(risingBall: PositionedBall): SettleSimulation {
  const engine = Matter.Engine.create({
    gravity: { x: 0, y: -3, scale: 0.001 },
    enableSleeping: false,
    positionIterations: 10,
    velocityIterations: 8,
  });

  Matter.Composite.add(engine.world, createJarWalls());

  const risingBody = Matter.Bodies.circle(risingBall.cx, risingBall.cy, risingBall.r + STROKE_HALF, {
    restitution: 0.15,
    friction: 0.1,
    frictionAir: 0.005,
  });

  Matter.Composite.add(engine.world, [risingBody]);
  const runner = Matter.Runner.create();
  return { engine, bodies: [risingBody], runner };
}

/**
 * Create a simulation where a ball drops back into the jar from above (normal gravity).
 * Existing balls are static obstacles.
 */
export function createDropSimulation(
  r: number,
  dropFromX: number,
  dropFromY: number,
  existingBalls: PositionedBall[],
): SettleSimulation {
  const engine = Matter.Engine.create({
    gravity: { x: 0, y: 1.5, scale: 0.001 },
    enableSleeping: true,
    positionIterations: 10,
    velocityIterations: 8,
  });

  Matter.Composite.add(engine.world, createJarWalls());

  const neckMinX = 68 + r + STROKE_HALF + 1;
  const neckMaxX = 152 - r - STROKE_HALF - 1;
  const clampedX = Math.max(neckMinX, Math.min(neckMaxX, dropFromX));

  const dropBody = Matter.Bodies.circle(clampedX, dropFromY, r + STROKE_HALF, {
    restitution: 0.1,
    friction: 0.4,
    frictionAir: 0.02,
    density: 0.004,
    sleepThreshold: 30,
  });

  const staticBodies = existingBalls.map((b) =>
    Matter.Bodies.circle(b.cx, b.cy, b.r + STROKE_HALF, { isStatic: true }),
  );

  Matter.Composite.add(engine.world, [dropBody, ...staticBodies]);
  const runner = Matter.Runner.create();
  return { engine, bodies: [dropBody, ...staticBodies], runner };
}

/**
 * Step a single-ball simulation forward by one frame.
 * Returns the first (dynamic) body's position and speed.
 */
export function stepSingleBallSimulation(sim: SettleSimulation): { cx: number; cy: number; speed: number } {
  Matter.Engine.update(sim.engine, 1000 / 60);
  const body = sim.bodies[0];
  return { cx: body.position.x, cy: body.position.y, speed: body.speed };
}
